"use client"
import { useState } from "react"
import { PLAN_CONFIG } from "@/lib/payments/plans"
import { usePaywall } from "@/lib/hooks/usePaywall"

// Real response shape of GET /api/v1/listings — mirrors the `select` in
// src/app/api/v1/listings/route.ts. The previous sample advertised trustScore,
// agent KYC/EAC/responseRate and a `suburbs` block, none of which this API
// returns; a bank integrating against it would have built to a fiction.
const sampleResponse = `{
  "success": true,
  "page": 1,
  "pageSize": 50,
  "data": [
    {
      "id": "clp9x2k4m0001t9...",
      "title": "4-bed house in Borrowdale",
      "status": "LIVE",
      "price": 285000,
      "priceType": "SALE",
      "propertyType": "HOUSE",
      "suburb": "Borrowdale",
      "city": "Harare",
      "beds": 4,
      "baths": 3,
      "isFeatured": false,
      "featuredUntil": null,
      "publishedAt": "2026-07-14T09:22:11.000Z",
      "createdAt": "2026-07-12T16:04:52.000Z"
    }
  ]
}`

// Describes ONLY what /api/v1/{listings,leads,market-stats} actually return today.
const features = [
  { title: "Your listings", desc: "GET /v1/listings returns the listings on your own account: title, status, price, type, suburb, city, beds, baths, featured state and dates. Paginated up to 100 per page." },
  { title: "Your leads", desc: "GET /v1/leads returns enquiries received on your listings, so you can pull them into your own CRM instead of working from the dashboard inbox." },
  { title: "Aggregate market stats", desc: "GET /v1/market-stats returns listing counts and median asking price grouped by city, property type and sale/rent across all live listings. Aggregates only — no per-listing or per-person data." },
  { title: "Key-based access you control", desc: "Authenticate with a Bearer key you mint and revoke yourself from your account settings. Revoking a key blocks it immediately." },
]

const useCases = [
  "Sync your listings into your own website or CRM instead of maintaining two systems",
  "Pull enquiries straight into your sales pipeline as they arrive",
  "Benchmark your asking prices against median market prices by city and property type",
  "Build internal reporting on your portfolio without exporting spreadsheets by hand",
]

export default function ApiProductPage() {
  // API access is a MARKET_LEADER entitlement — see api/account/api-keys/route.ts,
  // which rejects every other plan with a checkout link to MARKET_LEADER. This page
  // used to quote the DEVELOPER price, so a bank read "$60/month", applied, and was
  // then quoted $350. Quote the plan that actually grants the thing being sold.
  const { trialActive, pricing } = usePaywall()
  const apiMonthly = pricing?.MARKET_LEADER?.monthlyPrice ?? PLAN_CONFIG.MARKET_LEADER.monthlyPrice
  const apiPriceLabel = trialActive ? "Free right now" : `$${apiMonthly}`
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState({ company: "", contactName: "", email: "", phone: "", useCase: "" })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function handleSubmit() {
    if (!form.company || !form.contactName || !form.email) { setError("Company, contact name, and email are required"); return }
    setSubmitting(true); setError("")
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.contactName,
          email: form.email,
          phone: form.phone || undefined,
          subject: "API Access Request",
          message: `Company: ${form.company}\nContact: ${form.contactName}\nEmail: ${form.email}\nPhone: ${form.phone || "-"}\n\nUse case:\n${form.useCase}`,
        }),
      })
      const data = await res.json()
      if (data.success) setSubmitted(true)
      else setError(data.error ?? "Something went wrong")
    } catch { setError("Something went wrong. Please try again.") }
    setSubmitting(false)
  }

  return (
    <div style={{ background: "var(--cream-warm)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "72px 24px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 20 }}>For agencies on Market Leader</span>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 48, fontWeight: 700, color: "var(--cream)", lineHeight: 1.15, marginBottom: 20 }}>
            Zim Estates API
          </h1>
          <p style={{ fontSize: 17, color: "rgba(253,251,247,0.75)", lineHeight: 1.7, maxWidth: 600, margin: "0 auto 40px" }}>
            Pull your listings and enquiries into your own systems, and benchmark against live market aggregates. Included with the Market Leader plan.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setFormOpen(true)} style={{ padding: "16px 40px", background: "var(--gold)", color: "var(--navy)", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Request API access
            </button>
            <div style={{ padding: "16px 24px", background: "rgba(253,251,247,0.1)", border: "1px solid rgba(253,251,247,0.2)", borderRadius: 12, fontSize: 16, fontWeight: 600, color: "var(--cream)" }}>{apiPriceLabel} / month</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "64px 24px" }}>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 700, color: "var(--navy)", marginBottom: 8, textAlign: "center" }}>What the API provides</h2>
        <p style={{ color: "var(--muted)", textAlign: "center", fontSize: 15, marginBottom: 48 }}>RESTful JSON API. Authenticate with a Bearer key you generate and revoke from your account settings.</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20, marginBottom: 64 }}>
          {features.map((f, i) => (
            <div key={i} style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 28 }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>{f.title}</div>
              <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 64 }}>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "var(--navy)", marginBottom: 24 }}>Sample API response</h2>
          <pre style={{ background: "var(--navy)", color: "#a8c4ff", padding: "28px 32px", borderRadius: 16, fontSize: 13, lineHeight: 1.7, overflowX: "auto", fontFamily: "'Courier New', monospace", margin: 0 }}>
            {sampleResponse}
          </pre>
        </div>

        <div style={{ marginBottom: 64 }}>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "var(--navy)", marginBottom: 24 }}>Common use cases</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {useCases.map((u, i) => (
              <div key={i} style={{ display: "flex", gap: 14, background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 12, padding: "18px 22px" }}>
                <span style={{ color: "var(--gold-deep)", fontWeight: 700, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.5 }}>{u}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 20, padding: "40px 48px", textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Ready to integrate?</div>
          <p style={{ color: "var(--muted)", fontSize: 15, marginBottom: 8 }}>{apiPriceLabel} per month on the Market Leader plan, which includes API access. Cancel anytime.</p>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 28 }}>Need something the endpoints above do not cover? Tell us what you are building and we will talk it through.</p>
          <button onClick={() => setFormOpen(true)} style={{ padding: "16px 48px", background: "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Request API access
          </button>
        </div>
      </div>

      {formOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 24 }} onClick={() => setFormOpen(false)}>
          <div style={{ background: "var(--cream)", borderRadius: 20, padding: 40, maxWidth: 480, width: "100%", boxShadow: "var(--shadow-lg)", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--green-text)", marginBottom: 12 }}>Request received</div>
                <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>Our team will review your request and contact you within 48 hours with API credentials and documentation.</p>
                <button onClick={() => { setFormOpen(false); setSubmitted(false) }} style={{ padding: "12px 28px", background: "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>Request API access</div>
                <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>Tell us about your institution and use case. We review all applications within 48 hours.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {[
                    { label: "Company name", key: "company", placeholder: "e.g. CBZ Bank, CABS, etc.", type: "text" },
                    { label: "Contact name", key: "contactName", placeholder: "Your full name", type: "text" },
                    { label: "Work email", key: "email", placeholder: "you@company.com", type: "email" },
                    { label: "Phone (optional)", key: "phone", placeholder: "+263 77 ...", type: "tel" },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{f.label}</label>
                      <input value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder} type={f.type} style={{ width: "100%", padding: "11px 13px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none", boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Intended use case</label>
                    <textarea value={form.useCase} onChange={e => setForm(prev => ({ ...prev, useCase: e.target.value }))} placeholder="Describe how you plan to use the API..." rows={3} style={{ width: "100%", padding: "11px 13px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", background: "rgba(196,68,68,0.06)", borderRadius: 8, padding: "10px 14px" }}>{error}</div>}
                  <button onClick={handleSubmit} disabled={submitting} style={{ padding: 14, background: submitting ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                    {submitting ? "Submitting..." : "Submit request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
